import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StationSurchargeModel } from '@railmybox/api-pricing';
import { compareDates } from '@railmybox/shared/util';
import dayjs from 'dayjs/esm';

type ChangedValues = Record<string, any>;

@Injectable({ providedIn: 'root' })
export class AdditionalSurchargeFormService {
  private fb = inject(FormBuilder);

  createBaseForm(makeRequired = false): FormGroup {
    const requiredValidator = makeRequired ? Validators.required : null;

    return this.fb.group({
      customs: [undefined, requiredValidator],
      dangerousGoods: [undefined, requiredValidator],
      waste: [undefined, requiredValidator],
      container45ft: [undefined, requiredValidator],
      seal: [undefined, requiredValidator],
      solasVgm: [undefined, requiredValidator],
      bhtNumber: [undefined],
      heavyLiftTruck: [undefined, requiredValidator],
      heavyLiftTruckWeight: [undefined, requiredValidator],
      sideLoaderAvailable: [false],
      tipper20ftAvailable: [false],
      tipper40ftAvailable: [false],
      sideLoader: [{ value: undefined, disabled: true }],
      sideLoaderHighCube: [{ value: undefined, disabled: true }],
      tipper20ft: [{ value: undefined, disabled: true }],
      tipper40ft: [{ value: undefined, disabled: true }],
    });
  }

  createExtendedForm(makeRequired = false): FormGroup {
    const form = this.createBaseForm(makeRequired);

    form.addControl('validFrom', this.fb.control(undefined, Validators.required));
    form.addControl('validTo', this.fb.control({ value: undefined, disabled: true }));

    form.setValidators([compareDates('validFrom', 'validTo')]);
    form.updateValueAndValidity({ onlySelf: true, emitEvent: false });

    return form;
  }

  createSurcharge(
    changedValues: ChangedValues,
    baseSurcharge: Partial<StationSurchargeModel> | null | undefined,
    stationId: string
  ): StationSurchargeModel {
    const withCurrency = (field: keyof ChangedValues) =>
      changedValues?.[field] !== undefined ? { amount: Number(changedValues[field]), currency: 'EUR' } : (baseSurcharge?.[field] ?? null);

    return {
      ...baseSurcharge,
      stationId,
      customs: withCurrency('customs'),
      dangerousGoods: withCurrency('dangerousGoods'),
      waste: withCurrency('waste'),
      container45ft: withCurrency('container45ft'),
      seal: withCurrency('seal'),
      solasVgm: withCurrency('solasVgm'),
      bhtNumber: withCurrency('bhtNumber'),
      heavyLiftTruck: withCurrency('heavyLiftTruck'),
      sideLoader: withCurrency('sideLoader'),
      tipper20ft: withCurrency('tipper20ft'),
      sideLoaderHighCube: withCurrency('sideLoaderHighCube'),
      tipper40ft: withCurrency('tipper40ft'),

      heavyLiftTruckWeight: changedValues?.heavyLiftTruckWeight ?? baseSurcharge?.heavyLiftTruckWeight ?? null,
    };
  }

  isSurchargeOverlapWithAdditional(addStart: string | Date, surchargeStart: string | Date, surchargeEnd?: string | Date | null): boolean {
    const aStart = dayjs(addStart);
    const sStart = dayjs(surchargeStart);
    const sEnd = surchargeEnd ? dayjs(surchargeEnd) : null;

    // Open-ended surcharge: overlap if addStart >= surchargeStart
    if (!sEnd) {
      return aStart.isSame(sStart, 'day') || aStart.isAfter(sStart, 'day');
    }

    // Surcharge with end: overlap if surchargeStart <= addStart <= surchargeEnd
    return (aStart.isAfter(sStart, 'day') || aStart.isSame(sStart, 'day')) && (aStart.isBefore(sEnd, 'day') || aStart.isSame(sEnd, 'day'));
  }

  getMatchingSurcharge<T extends { validFrom?: string | Date; validTo?: string | Date | null }>(
    tariffStart: string | Date,
    stationSurcharges: T[]
  ): T | Record<string, never> {
    if (!tariffStart || !stationSurcharges.length) return {};

    const validItems = stationSurcharges.filter((s): s is T & { validFrom: string | Date } => Boolean(s.validFrom));

    const match = validItems.find((s) => this.isSurchargeOverlapWithAdditional(tariffStart, s.validFrom, s.validTo ?? null));

    const fallback = validItems
      .filter((s) => {
        const from = dayjs(s.validFrom);
        return from.isBefore(dayjs(tariffStart), 'day') || from.isSame(dayjs(tariffStart), 'day');
      })
      .sort((a, b) => dayjs(b.validFrom).diff(dayjs(a.validFrom)))[0];

    return match || fallback || {};
  }

  getDirtyValues(form: FormGroup): any {
    const dirtyValues: any = {};

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control?.dirty) {
        if (control instanceof FormGroup) {
          dirtyValues[key] = this.getDirtyValues(control);
        } else {
          dirtyValues[key] = control.value;
        }
      }
    });

    return dirtyValues;
  }
}
